// PROVISOIRE — rubriques de la fiche de décision que l'API v0 ne porte pas encore (§ 11) : paramétrage réellement
// appliqué et ses écarts, devis ramenés à des unités, compléments des retours d'expérience (facture, incidents,
// dérives du paramétrage). Elles sont gardées dans la MÉMOIRE de l'onglet, le temps de la session seulement (constat
// O42) : jamais dans localStorage ni sessionStorage, effacées à la déconnexion, à l'expiration de la session et au
// rechargement de la page. Sur un poste partagé, la personne suivante ne les lit donc pas. Elles ne sont ni
// partagées, ni sauvegardées, ni journalisées ; l'interface le signale à chaque fois.

export interface RegleParametrage {
  regle: string;
  appliquee: boolean;
  /** Écart aux recommandations (ANSSI, éditeur, CIS) : justification, qui l'a accepté, jusqu'à quand. */
  ecart?: { justification: string; accepte_par: string; jusqu_au: string } | null;
}

export interface Devis {
  fournisseur: string;
  montant: number;
  quantite: number;
  unite: string;
  date: string;
}

export interface ComplementRetour {
  facture?: number | null;
  incidents?: string;
  derives?: string;
}

export interface ComplementsDecision {
  parametrage: RegleParametrage[];
  devis: Devis[];
  retours: Partial<Record<'3' | '6' | '12', ComplementRetour>>;
}

export const VIDE: ComplementsDecision = { parametrage: [], devis: [], retours: {} };

/** Ce que les compléments attendent d'un stockage (la mémoire de session ; un double dans les tests). */
export type Stockage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'>;

/** Mémoire de la session : une table JavaScript, perdue au rechargement, vidée à la déconnexion. */
export class MemoireSession implements Stockage {
  private readonly m = new Map<string, string>();
  getItem(k: string): string | null {
    return this.m.get(k) ?? null;
  }
  setItem(k: string, v: string): void {
    this.m.set(k, v);
  }
  removeItem(k: string): void {
    this.m.delete(k);
  }
  clear(): void {
    this.m.clear();
  }
  get taille(): number {
    return this.m.size;
  }
}

/** La mémoire de session de l'application (une par onglet). */
export const MEMOIRE = new MemoireSession();

const PREFIXE = 'ekera.complements.';
const cle = (carte: string, decision: string) => `${PREFIXE}${carte}.${decision}`;

export function lireComplements(carte: string, decision: string, stockage: Stockage | null = MEMOIRE): ComplementsDecision {
  try {
    const brut = stockage?.getItem(cle(carte, decision));
    if (!brut) return structuredClone(VIDE);
    const v = JSON.parse(brut) as Partial<ComplementsDecision>;
    return { parametrage: v.parametrage ?? [], devis: v.devis ?? [], retours: v.retours ?? {} };
  } catch {
    return structuredClone(VIDE);
  }
}

/** Rend vrai si l'enregistrement (en mémoire) a réussi. */
export function ecrireComplements(
  carte: string,
  decision: string,
  c: ComplementsDecision,
  stockage: Stockage | null = MEMOIRE,
): boolean {
  try {
    if (!stockage) return false;
    stockage.setItem(cle(carte, decision), JSON.stringify(c));
    return true;
  } catch {
    return false;
  }
}

/** Fin de session (déconnexion, session expirée, changement de personne) : plus rien ne reste. */
export function effacerComplements(stockage: Stockage = MEMOIRE): void {
  stockage.clear();
}

/**
 * Efface ce que les versions précédentes de l'application gardaient dans le navigateur (localStorage, en clair, sans
 * échéance : constat O42). Appelé au démarrage ; rend le nombre d'entrées effacées.
 */
export function purgerAnciensComplements(stockage: Storage | null = navigateur()): number {
  if (!stockage) return 0;
  try {
    const cles: string[] = [];
    for (let i = 0; i < stockage.length; i++) {
      const k = stockage.key(i);
      if (k?.startsWith(PREFIXE)) cles.push(k);
    }
    for (const k of cles) stockage.removeItem(k);
    return cles.length;
  } catch {
    return 0;
  }
}

function navigateur(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Montant ramené à l'unité (par utilisateur, par serveur, par site…) pour comparer des devis. */
export function montantParUnite(d: Devis): number | null {
  return d.quantite > 0 ? d.montant / d.quantite : null;
}
